// The ORION roll WINDOW: how the landmine map is read, and which span the calendar is asked about.
// CR-20260904-001, team Filtering rules (units F3 + F5).
//
// Pure / Angular-free on purpose: every rule below is decided here and unit-tested directly, so the
// APG host and the skill stay thin glue. ES5 note: arrays + indexOf only (no Set / for..of).
//
// WHAT USED TO BE HERE AND IS NOW GONE: stand-ins for contracts C2 and C2b
// (`resolveWindowExpirations`, `resolveUserExcludedDates`) and for the weekday overrides. Input
// validation's branch is merged, so callers import the real ones from `apg-copilot-questionnaire.ts`
// (`resolveWindowExpirations`, `resolveUserExcludedDates`, `weekdayOverrideKeys`). Their C2 is derived
// as *window minus survivors*, so the partition invariant holds by construction and nothing here has
// to defend against overlap.
//
// ⚠ ALSO GONE, 2026-09-08, ON THE OWNER'S INSTRUCTION: the BUFFER behind a landmine (built 2026-09-07:
// D+1 and D+2 disqualified as well, first offerable date D+3) and the two-day span look-back that
// existed only as its input. THERE ARE NO BUFFER DAYS. Only a Tier 1 or Tier 2 day itself is removed
// by the landmine rules; every other day in the window is priced and ranked on its per-day credit.
// Do not reintroduce a look-back or a "days behind a landmine" rule here without a new owner ruling.

import { RollPositionsInputs } from './apg-copilot-domain';
import { dayKey, isValidDate } from './apg-copilot-questionnaire';

// ⚠ WHAT LEFT THIS FILE ON 2026-09-10 (CR-20260910-001 milestone 1, contract A1 §4): the three
// VERDICT READERS - `isDisqualifiedMonday` (the Monday step of the disqualification chain),
// `tierOf` (the "absence is never clear" guard) and `landmineDayOf` (the row the reason line is
// composed from). They were the run's brain, and the run now happens on the backend, where
// `ILandmineCalendar.GetDays` answers the same questions over the same window with no buffer days.
// Do not re-add a verdict reader here: a second implementation of "is this day clear" is a second
// answer, and this file's whole history is about that question having exactly one.
//
// WHAT STAYED, AND WHY THE FILE IS NOT DELETED: everything below. The SPAN half never belonged to
// the run. It belongs to the PRICE BOX'S OWN COLUMN COLOURING, which colours columns before ORION
// has ever run and keeps colouring them when it never runs at all. `LandmineDaysService` keys its
// cache on EXACT span equality, so `stabiliseSpan` is load-bearing whatever the run does.

// ---- the span the calendar is asked about -----------------------------------------------------
//
// WHAT IS ASKED FOR, AND WHY IT IS NOT ROUNDED.
// Owner ruling O60 (2026-09-07): "ORION must ask Calendar for the request range, not rounding it up
// to a month/quarter/week etc. Ask for a range + some buffer if necessary."
//
// So a span is EXACTLY the dates in question. Nothing is necessary beyond them: the only rule that
// ever looked outside the requested range - the buffer behind a landmine - was rolled back on
// 2026-09-08, so there is no look-back either.
//
// ⚠ WHAT USED TO BE HERE, AND WHY IT IS GONE: `quantizeSpanToMonths()` widened every span to whole
// months so that repeated column builds produced an identical cache key (`LandmineDaysService` keys
// on EXACT span equality - the literal string `from + '|' + to`, not containment). It fetched four
// weeks of calendar to answer about five days. The stability it was providing is carried by
// `stabiliseSpan()` below, which was always doing the load-bearing part: the host HOLDS the span it
// has asked about and only ever widens, returning the SAME object whenever the held span already
// covers what is needed. Distinct keys are therefore bounded by the number of genuine WIDENINGS, not
// by the number of repaints. Do not re-introduce the rounding.

export interface LandmineSpan {
    from: string;
    to: string;
}

/** True for a well-formed "yyyy-MM-dd" label. A key we cannot place must never widen a span. */
function isDayKey(key: string): boolean {
    return !!key && /^\d{4}-\d{2}-\d{2}$/.test(key);
}

/**
 * The span for a run: the trader's entered dates, exactly. A range gives start..end (normalised, so
 * an inverted range asks the question he meant); a single date gives itself. Null when the dates are
 * not yet a real selection - there is nothing to ask about, and asking anyway would cache an answer
 * under a meaningless key.
 */
export function landmineWindowSpan(i: RollPositionsInputs): LandmineSpan | null {
    if (!i) {
        return null;
    }

    if (i.date.kind === 'single') {
        if (!isValidDate(i.date.single)) {
            return null;
        }
        const key = dayKey(i.date.single as Date);
        return { from: key, to: key };
    }

    const start = i.date.start;
    const end = i.date.end;
    if (!isValidDate(start) || !isValidDate(end)) {
        return null;
    }

    const a = dayKey(start as Date);
    const b = dayKey(end as Date);
    return a <= b ? { from: a, to: b } : { from: b, to: a };
}

/**
 * The span covering every day key handed in. This is the COLUMN-BUILD path: before the trader has
 * ever run ORION there is no requested window to derive from, and the Price Box still needs its
 * columns coloured. `stabiliseSpan()` is what stops the loaded columns - which move as data arrives
 * - from minting a new cache key on every repaint.
 */
export function landmineSpanOf(dateKeys: string[]): LandmineSpan | null {
    const keys = dateKeys || [];
    let from: string | null = null;
    let to: string | null = null;

    for (let ix = 0; ix < keys.length; ix++) {
        const k = keys[ix];
        if (!isDayKey(k)) {
            continue;                       // a key we cannot place cannot widen a span
        }
        if (from === null || k < from) {
            from = k;
        }
        if (to === null || k > to) {
            to = k;
        }
    }

    return from !== null && to !== null ? { from: from, to: to } : null;
}

/** True when `outer` answers every date `inner` covers. */
export function spanCovers(outer: LandmineSpan | null, inner: LandmineSpan | null): boolean {
    if (!outer || !inner) {
        return false;
    }
    return outer.from <= inner.from && outer.to >= inner.to;
}

/**
 * THE STABILITY MECHANISM: hold and widen. Since O60 removed the month rounding this is the ONLY
 * one - and it is the one that was doing the load-bearing work all along.
 *
 * A run's window and the Price Box's loaded columns are different questions, and left raw they
 * would alternate as the trader runs and the grid rebuilds, fighting each other for a cache keyed
 * on EXACT span equality. So the host HOLDS the span it has asked about and only ever widens it:
 *
 *   - nothing needed         -> keep what is held (a repaint with no columns must not clear it);
 *   - held already covers it -> **the held span, character for character**, so the key hits cache;
 *   - genuinely wider        -> the union, which becomes the new held span.
 *
 * This bounds the number of distinct spans by the number of WIDENINGS rather than by the number of
 * builds - and `LandmineDaysService` is provided in `AdjustmentPricingGridModule`, so a per-host
 * held span has exactly the same lifetime as the cache it is protecting.
 */
export function stabiliseSpan(
    held: LandmineSpan | null,
    needed: LandmineSpan | null
): LandmineSpan | null {

    if (!needed) {
        return held;
    }
    if (!held) {
        return needed;
    }
    if (spanCovers(held, needed)) {
        return held;                        // the SAME object, so the SAME key string
    }

    return {
        from: held.from <= needed.from ? held.from : needed.from,
        to: held.to >= needed.to ? held.to : needed.to
    };
}

/** The cache key the service will build from a span - what a stability test actually compares. */
export function spanKey(span: LandmineSpan | null): string {
    return span ? span.from + '|' + span.to : '';
}
